import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { Card, Spin, Typography, Space, Collapse, Button, Row, Col } from 'antd';
import { EyeOutlined, EditOutlined } from '@ant-design/icons';
import { DIRECTORY } from '../../graphql/queries/directories';
import { DirectoryResponse, DirectoryValue } from '../../types/directory';
import type { CollapseProps } from 'antd';

const { Text } = Typography;

const ValueContent: React.FC<{
  value: DirectoryValue;
  onView?: (e: React.MouseEvent) => void;
  onEdit?: (e: React.MouseEvent) => void;
}> = ({ value, onView, onEdit }) => (
  <Row justify="space-between" align="top" style={{ width: '100%' }}>
    <Col>
      <Text strong>{value.name}</Text>
      {value.description && <Text type="secondary">{value.description}</Text>}
    </Col>
    {value.directory && (
      <Col>
        <Space>
          <Button type="link" icon={<EyeOutlined />} onClick={onView} size="small">
            View
          </Button>
          <Button type="link" icon={<EditOutlined />} onClick={onEdit} size="small">
            Edit
          </Button>
        </Space>
      </Col>
    )}
  </Row>
);

export const DirectoryValueCard: React.FC<{ value: DirectoryValue }> = ({ value }) => {
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const {
    loading: subDirLoading,
    data: subDirData,
    refetch: refetchSubDir,
  } = useQuery<DirectoryResponse>(DIRECTORY, {
    variables: { id: value.directory },
    skip: !value.directory || !expanded || value.directory === '',
  });

  useEffect(() => {
    if (expanded && value.directory) {
      refetchSubDir();
    }
  }, [expanded, value.directory]);

  const handleAction = (action: 'view' | 'edit') => (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/admin/directory/${action === 'edit' ? 'edit/' : ''}${value.directory}`);
  };

  if (!value.directory) {
    return (
      <Card size="small" style={{ marginBottom: 8 }}>
        <ValueContent value={value} />
      </Card>
    );
  }

  const items: CollapseProps['items'] = [
    {
      key: value.id!,
      label: (
        <ValueContent value={value} onView={handleAction('view')} onEdit={handleAction('edit')} />
      ),
      children: subDirLoading ? (
        <Spin size="small" />
      ) : (
        subDirData?.directory.values.map((subValue: DirectoryValue) => (
          <DirectoryValueCard key={subValue.id} value={subValue} />
        ))
      ),
    },
  ];

  return (
    <Collapse
      items={items}
      style={{ marginBottom: 8 }}
      onChange={keys => setExpanded(keys.includes(value.id!))}
    />
  );
};
